// Registro de horas v2 (docs/SPEC.md «Cambios v2»): un registro por persona, tarjeta y día.
// Registrar fija el total; el selector mueve de 1 en 1 h con 0,5 h como mínimo, y «Especificar»
// acepta horas libres con hasta 2 decimales. Debe coincidir con public.set_work_log() en SQL.

export const MIN_HOURS = 0.5;
export const MAX_HOURS = 24;
/** Lo que propone la hoja cuando la tarjeta aún no tiene registro ese día. */
export const DEFAULT_HOURS = 1;

// EPSILON: 1.255 * 100 es 125.4999… en binario y redondearía hacia abajo.
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export type ParsedHours = { ok: true; hours: number } | { ok: false; error: string | null };

/** Horas escritas a mano: coma o punto; entre 0,5 y 24. Vacío no es válido, pero no es error. */
export function parseHours(text: string): ParsedHours {
  const raw = text.trim().replace(',', '.');
  if (raw === '') return { ok: false, error: null };
  if (!/^\d+(\.\d+)?$/.test(raw))
    return { ok: false, error: 'Escribe un número, por ejemplo 1,2.' };
  const hours = round2(Number(raw));
  if (hours < MIN_HOURS) return { ok: false, error: 'El mínimo es 0,5 h.' };
  if (hours > MAX_HOURS) return { ok: false, error: 'El máximo es 24 h.' };
  return { ok: true, hours };
}

/** Paso del selector: ±1 h, con 0,5 h como mínimo (1 ↔ 0,5) y 24 h como máximo. */
export function stepHours(current: number, direction: 1 | -1): number {
  if (direction < 0) return Math.max(MIN_HOURS, round2(current - 1));
  if (current < 1) return 1;
  return Math.min(MAX_HOURS, round2(current + 1));
}

export function hoursToSeconds(hours: number): number {
  return Math.round(hours * 3600);
}

export function secondsToHours(seconds: number): number {
  return round2(seconds / 3600);
}

/** '0,5 h', '1,2 h', '3 h'. */
export function formatHoursValue(hours: number): string {
  return `${String(round2(hours)).replace('.', ',')} h`;
}

export interface DayLog {
  card_id: string;
  work_date: string;
  seconds: number;
  deleted_at: string | null;
}

/**
 * Pantalla Hoy: los registros vivos de un día (de una persona), del más largo al más corto, y
 * su total. Es la misma suma que hacen los reportes sobre work_logs.
 */
export function dayTotals<T extends DayLog>(
  logs: readonly T[],
  date: string,
): { seconds: number; rows: T[] } {
  const rows = logs
    .filter((l) => l.work_date === date && !l.deleted_at)
    .sort((a, b) => b.seconds - a.seconds || a.card_id.localeCompare(b.card_id));
  return { seconds: rows.reduce((s, l) => s + l.seconds, 0), rows };
}

/** Lo que sumó un cronómetro: minutos bajo una hora ('42 min'), horas desde ahí ('1,5 h'). */
export function formatAddedSeconds(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return formatHoursValue(minutes / 60);
}
